"use client";
import {
  BMButton,
  BMButtonIcon,
  BMCard,
  BMOverlay,
  BMPrimitives,
} from "@traveloka/district-bloom";
import IcSystemCrossClose16 from "@traveloka/icon-kit-web/react/IcSystemCrossClose16";
import { ComponentRef, useMemo, useRef } from "react";
import {
  Animated,
  ImageSourcePropType,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import { appendTestId } from "../../utils";
import Portal from "../Portal";
import BMView from "../View/BMView";
import {
  CAROUSEL_CARD_HEIGHT,
  DEFAULT_WIDTH,
  OFFSCREEN_Y,
  POSITION_STYLES,
  SpotlightPosition,
} from "./constants";
import SpotlightCardBody from "./SpotlightCardBody";
import SpotlightCarousel from "./SpotlightCarousel";
import type { SpotlightPagination, SpotlightPaginationItem } from "./types";
import useSpotlightAnimation from "./useSpotlightAnimation";
import useSpotlightCarousel from "./useSpotlightCarousel";

export type { SpotlightPagination, SpotlightPaginationItem };

type Props = {
  visible?: boolean;
  position?: SpotlightPosition;
  width?: ViewStyle["width"];
  withOverlay?: boolean;
  testID?: string;
  onClose?: () => void;
  pagination?: SpotlightPagination;

  // Legacy logic, will remove after train product popover is commented/deleted
  /** @deprecated  */
  title?: string;
  /** @deprecated  */
  description?: string;
  /** @deprecated  */
  imageSource?: ImageSourcePropType;
  /** @deprecated  */
  buttonLabel?: string;
};

export default function Spotlight({
  visible = false,
  position = "bottom-right",
  width = DEFAULT_WIDTH,
  withOverlay = false,
  testID: initialTestID,
  onClose,
  pagination,
}: Props) {
  const ref = useRef<ComponentRef<typeof View>>(null);
  const { mounted, animatedStyle } = useSpotlightAnimation(
    visible,
    OFFSCREEN_Y
  );

  const testID = useMemo(
    () => appendTestId("spotlight", initialTestID),
    [initialTestID]
  );

  const {
    isCarousel,
    activeItem,
    cardButtonLabel,
    handleSoftDismiss,
    carouselProps,
  } = useSpotlightCarousel({
    pagination: pagination ??
      // legacy logic, will remove after train product popover is commented/deleted
      {
        items: [],
      },
    visible,
    width,
    onClose,
  });

  const styles = useMemo(
    () => createStyles(position, width, isCarousel),
    [position, width, isCarousel]
  );

  if (!mounted) return null;

  const showCloseButton = isCarousel && position !== "center";

  return (
    <Portal>
      <BMView style={styles.wrapper} pointerEvents="box-none">
        {withOverlay && (
          <BMOverlay
            visible={visible}
            dismissible={false}
            onDismiss={onClose}
            testID={appendTestId("overlay", testID)}
          />
        )}
        <Animated.View
          style={[styles.position, animatedStyle]}
          ref={ref}
          pointerEvents={position === "center" ? "box-none" : "auto"}
        >
          <BMCard
            role="dialog"
            layout={isCarousel ? "SPACIOUS" : "COMPACT"}
            elevation="FLOAT"
            style={styles.container}
          >
            {showCloseButton && (
              <BMView style={styles.closeButton}>
                <BMButtonIcon
                  Icon={IcSystemCrossClose16}
                  size="NORMAL"
                  onPress={handleSoftDismiss}
                  testID={appendTestId("button_close", testID)}
                />
              </BMView>
            )}
            {isCarousel ? (
              <SpotlightCarousel
                items={pagination?.items ?? []}
                imageStyle={styles.image}
                testID={testID}
                {...carouselProps}
              />
            ) : (
              <SpotlightCardBody
                style={styles.content}
                title={activeItem?.title}
                description={activeItem?.description}
                imageSource={activeItem?.imageSource}
                imageStyle={styles.image}
                testID={testID}
              >
                {cardButtonLabel ? (
                  <BMView>
                    <BMButton
                      stretchable
                      size="LARGE"
                      label={cardButtonLabel}
                      onPress={() => onClose?.()}
                      testID={appendTestId("button", testID)}
                    />
                  </BMView>
                ) : null}
              </SpotlightCardBody>
            )}
          </BMCard>
        </Animated.View>
      </BMView>
    </Portal>
  );
}

function createStyles(
  position: SpotlightPosition,
  width: ViewStyle["width"],
  isCarousel: boolean
) {
  return StyleSheet.create({
    wrapper: {
      flex: 1,
      ...StyleSheet.absoluteFillObject,
    },
    position: {
      position: "absolute",
      bottom: 0,
      ...POSITION_STYLES[position],
    },
    container: {
      width,
      height: isCarousel ? CAROUSEL_CARD_HEIGHT : undefined,
      position: "relative",
      marginVertical: BMPrimitives.BMSize.xl5,
      marginHorizontal: position === "center" ? 0 : BMPrimitives.BMSize.xl3,
    },
    content: {
      padding: BMPrimitives.BMSize.md,
    },
    closeButton: {
      position: "absolute",
      top: BMPrimitives.BMSize.md,
      right: BMPrimitives.BMSize.md,
      zIndex: 1,
    },
    image: {
      width: "100%",
      height: 156,
    },
  });
}
